import React, { useState, useEffect } from 'react';
import { Customer, CreateCustomer, UpdateCustomer, CustomerType, CustomerStatus, Tag, CreateCustomerPersonDto } from '../types';
import { tagsAPI } from '../services/api';
import TagInput from './TagInput';
import DateInput from './DateInput';
import LocationPicker from './LocationPicker';
import CustomerPersons from './CustomerPersons';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (customer: CreateCustomer | UpdateCustomer, pendingPersons?: CreateCustomerPersonDto[]) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  onNewTagCreated?: (tag: Tag) => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSubmit,
  onCancel,
  isLoading = false,
  onNewTagCreated,
}) => {
  const [formData, setFormData] = useState<CreateCustomer | UpdateCustomer>({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    website: '',
    statusId: CustomerStatus.Active,
    type: undefined,
    isArchive: false,
    showInTimeTracking: false,
    officialId: '',
    country: '',
    longitude: undefined,
    latitude: undefined,
    group: '',
    tagIds: [],
  });

  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>([]);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [pendingPersons, setPendingPersons] = useState<CreateCustomerPersonDto[]>([]);

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    if (customer) {
      setFormData({
        ...customer,
        date: customer.date.split('T')[0],
        showInTimeTracking: customer.showInTimeTracking ?? false,
        tagIds: customer.tags?.map(tag => tag.id) || [],
      });
      setSelectedTags(customer.tags || []);
    }
  }, [customer]);

  const loadTags = async () => {
    try {
      const data = await tagsAPI.getTags();
      setTags(data);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    let newValue: any;
    if (type === 'checkbox') {
      newValue = checked;
    } else if (name === 'type') {
      newValue = value === '' ? undefined : parseInt(value);
    } else if (name === 'longitude' || name === 'latitude') {
      newValue = value === '' ? undefined : parseFloat(value);
    } else {
      newValue = value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleTagsChange = (newTags: Tag[]) => {
    setSelectedTags(newTags);
    setFormData(prev => ({
      ...prev,
      tagIds: newTags.map(tag => tag.id),
    }));
  };

  const handleNewTagCreated = (newTag: Tag) => {
    // Add the new tag to available tags list
    setTags(prev => [...prev, newTag]);
    // Notify parent component
    if (onNewTagCreated) {
      onNewTagCreated(newTag);
    }
  };

  const handleLocationChange = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat === 0 ? undefined : lat,
      longitude: lng === 0 ? undefined : lng,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // First, create any new tags
      const newTagIds: number[] = [];
      for (const tagName of selectedTagNames) {
        try {
          const newTag = await tagsAPI.createTag({ name: tagName });
          newTagIds.push(newTag.id);
          // Add to available tags list
          setTags(prev => [...prev, newTag]);
          // Notify parent component
          if (onNewTagCreated) {
            onNewTagCreated(newTag);
          }
        } catch (error) {
          console.error(`Failed to create tag "${tagName}":`, error);
          alert(`Failed to create tag "${tagName}". Please try again.`);
          return;
        }
      }

      // Update form data with all tag IDs (existing + new)
      const allTagIds = [...formData.tagIds, ...newTagIds];
      const updatedFormData = {
        ...formData,
        tagIds: allTagIds
      };

      // Submit the customer data with pending persons if creating new customer
      await onSubmit(updatedFormData, customer ? undefined : pendingPersons);
    } catch (error) {
      console.error('Error during submission:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  const typeOptions = [
    { value: CustomerType.Government, label: 'Government' },
    { value: CustomerType.Education, label: 'Education' },
    { value: CustomerType.Legal, label: 'Legal' },
    { value: CustomerType.Healthcare, label: 'Healthcare' },
    { value: CustomerType.Retail, label: 'Retail' },
    { value: CustomerType.Banking, label: 'Banking' },
    { value: CustomerType.Nonprofit, label: 'Nonprofit' },
    { value: CustomerType.Publishers, label: 'Publishers' },
    { value: CustomerType.Agriculture, label: 'Agriculture' },
    { value: CustomerType.Logistics, label: 'Logistics' },
    { value: CustomerType.Telecommunications, label: 'Telecommunications' },
    { value: CustomerType.Media, label: 'Media' },
    { value: CustomerType.Hospitality, label: 'Hospitality' },
  ];

  return (
    <div className="card customer-form-card">
      <div className="card-header">
        <h5 className="mb-0">
          {customer ? `Edit Customer: ${customer.title}` : 'Create New Customer'}
        </h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit} className="customer-form">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="title" className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isArchive"
                  name="isArchive"
                  checked={formData.isArchive}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <label className="form-check-label" htmlFor="isArchive">
                  Archive Customer
                </label>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="website" className="form-label">Website</label>
              <input
                type="url"
                className="form-control"
                id="website"
                name="website"
                value={formData.website || ''}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="officialId" className="form-label">Official ID (Tax ID)</label>
              <input
                type="text"
                className="form-control"
                id="officialId"
                name="officialId"
                value={formData.officialId || ''}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="Tax ID or registration number"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="country" className="form-label">Country</label>
              <input
                type="text"
                className="form-control"
                id="country"
                name="country"
                value={formData.country || ''}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="Ukraine, USA, etc."
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 mb-3">
              <label htmlFor="group" className="form-label">Group</label>
              <input
                type="text"
                className="form-control"
                id="group"
                name="group"
                value={formData.group || ''}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="Group classification"
              />
            </div>
          </div>

          <div className="mb-3">
            <LocationPicker
              latitude={formData.latitude}
              longitude={formData.longitude}
              onLocationChange={handleLocationChange}
              disabled={isLoading}
            />
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="date" className="form-label">
                Date <span className="text-danger">*</span>
              </label>
              <DateInput
                className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                id="date"
                name="date"
                value={formData.date}
                onChange={(value) => handleInputChange({ target: { name: 'date', value } } as any)}
                disabled={isLoading}
                required
              />
              {errors.date && <div className="invalid-feedback">{errors.date}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="type" className="form-label">
                Type
              </label>
              <select
                className="form-select"
                id="type"
                name="type"
                value={formData.type ?? ''}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Not selected</option>
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Tags</label>
              <TagInput
                selectedTags={selectedTags}
                onTagsChange={handleTagsChange}
                availableTags={tags}
                onNewTagCreated={handleNewTagCreated}
                disabled={isLoading}
                placeholder="Type to add or create tags..."
                selectedTagNames={selectedTagNames}
                onTagNamesChange={setSelectedTagNames}
              />
            </div>
            <div className="col-md-6 mb-3 d-flex align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showInTimeTracking"
                  name="showInTimeTracking"
                  checked={formData.showInTimeTracking}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <label className="form-check-label" htmlFor="showInTimeTracking">
                  Show in time tracking
                </label>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="description" className="form-label">Description</label>
            <textarea
              className="form-control"
              id="description"
              name="description"
              rows={3}
              value={formData.description || ''}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>

          <div className="mb-3">
            <CustomerPersons
              customerId={customer?.id}
              isEditing={true}
              onPendingPersonsChange={setPendingPersons}
            />
          </div>

          <div className="d-flex justify-content-start gap-2 form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : customer ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerForm;
